import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import Gap from '../components/Gap'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  bookmarkApi,
  type InstagramPlace,
  type InstagramResolveResponse,
  type InstagramResult,
} from '../api/bookmark'
import { useAuthStore } from '../stores/authStore'
import { shortenSido } from '../utils/address'
import { toastUtil } from '../utils/toast'
import SaveDestinationSheet from '../components/SaveDestinationSheet'
import { takeAfterCreate } from '../utils/platelistCreateReturn'
import { pageCache } from '../utils/pageCache'

/**
 * 인스타그램에서 공유해 들어오는 화면.
 *
 * 앱이 공유를 받으면 이 주소로 보낸다: /share/instagram?url=<인스타 링크>
 *
 * 생김새는 앱의 '장소 가져오기'와 같은 규칙을 쓴다 — 상호는 16px 굵게,
 * 주소는 14px, 목록은 px-20px py-16px gap-24px, 하단 버튼은 pb-32px.
 * 이 화면만 다른 모양이면 같은 앱으로 안 보인다.
 *
 * 고르는 규칙
 * ----------
 * 한 곳이면 바로 담고, 여러 곳이면 목록을 보여주고 고르게 한다. 상호 하나에
 * 카카오 후보가 여럿일 때만 그 상호를 머리말로 보여준다 — 후보가 하나뿐인데
 * 상호를 또 적으면 같은 말이 두 번 나온다.
 */

const InstagramShare: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const myUserId = useAuthStore((s) => s.userId)
  const url = params.get('url') ?? ''

  const shareCacheKey = `instagram-share:${url}`
  const [data, setData] = useState<InstagramResolveResponse | null>(
    () => pageCache.get<InstagramResolveResponse>(shareCacheKey)
  )
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDestSheetOpen, setIsDestSheetOpen] = useState(false)
  /** 저장이 끝난 뒤 받은 우리 쪽 장소 id. 시트는 이걸로 리스트를 넣고 뺀다 */
  const [savedPlaceIds, setSavedPlaceIds] = useState<number[]>([])
  /** 방금 만들고 돌아온 리스트 — 시트가 담고 체크한다 */
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null)

  /** 선택된 카카오 장소 id. 확정된 것은 처음부터 켜 둔다. */
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(pageCache.get<string[]>(`${shareCacheKey}:picked`) ?? [])
  )

  /*
   * 고른 것과 해석 결과를 기억해 둔다.
   *
   * 시트의 "추가" 로 나갔다 오면 이 화면이 새로 만들어진다. 기억해 두지
   * 않으면 인스타를 다시 읽어야 하고(2초), 사용자가 고른 것도 사라진다.
   * 앱 안에서만 오가므로 이 기억은 그대로 남아 있다.
   */
  useEffect(() => {
    if (data) pageCache.set(shareCacheKey, data)
  }, [data, shareCacheKey])
  useEffect(() => {
    pageCache.set(`${shareCacheKey}:picked`, Array.from(selected))
  }, [selected, shareCacheKey])

  // 같은 url 로 두 번 해석하지 않는다 (StrictMode 의 이중 호출 포함)
  const requested = useRef<string | null>(null)

  /*
   * 리스트를 새로 만들고 돌아왔는가.
   *
   * 돌아오면 이 화면이 새로 만들어진다. 저장은 떠나기 전에 이미 끝났으므로
   * 인스타를 다시 읽을 필요가 없다 — 바로 담기 시트를 다시 연다. 새로 만든
   * 리스트에는 그 장소들을 담아 둔다(그러려고 만든 것이다).
   */
  useEffect(() => {
    const back = takeAfterCreate(`instagram:${url}`)
    if (!back) return

    const ids = (back.payload as { placeIds?: number[] } | undefined)?.placeIds ?? []
    requested.current = url // 다시 해석하지 않는다
    setSavedPlaceIds(ids)
    setIsDestSheetOpen(true)

    setJustCreatedId(back.createdId)
    // 주소가 바뀌면 다른 글이다
  }, [url])

  useEffect(() => {
    if (url === '' || requested.current === url) return
    requested.current = url
    // 기억해 둔 결과가 있으면 다시 읽지 않는다. state(data) 대신 pageCache 를
    // 보는 이유는, 방금 받아 넣은 결과 때문에 이 effect 가 다시 돌면 안 되기
    // 때문이다 — 여기서 알고 싶은 건 "들어올 때 이미 있었나" 뿐이다.
    if (pageCache.get<InstagramResolveResponse>(shareCacheKey)) return

    let alive = true
    bookmarkApi
      .resolveInstagramShare(url)
      .then((res) => {
        if (!alive) return
        setData(res)
        setSelected(
          new Set(
            res.results
              .filter((r) => r.status === 'confirmed' && r.place)
              .map((r) => r.place!.kakao_place_id)
          )
        )
        notifyExcluded(res.results)
      })
      .catch((e) => {
        if (!alive) return
        setError(e?.response?.data?.message ?? '인스타그램 글을 읽지 못했어요.')
      })

    return () => {
      alive = false
    }
  }, [url, shareCacheKey])

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  /**
   * 한 상호 안에서는 하나만 고를 수 있다.
   * 같은 가게의 서로 다른 지점을 둘 다 담을 이유가 없다.
   */
  const pickOne = useCallback((group: InstagramPlace[], id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      group.forEach((p) => next.delete(p.kakao_place_id))
      next.add(id)
      return next
    })
  }, [])

  /** 담을 수 있는 것만. 못 찾았거나 음식점이 아닌 것은 목록에 두지 않는다. */
  const rows = useMemo(
    () =>
      (data?.results ?? []).filter(
        (r) =>
          (r.status === 'confirmed' || r.status === 'ambiguous') &&
          r.candidates.length > 0
      ),
    [data]
  )

  const chosenIds = useMemo(
    () =>
      rows
        .flatMap((r) => r.candidates)
        .filter((p) => selected.has(p.kakao_place_id))
        .map((p) => p.kakao_place_id),
    [rows, selected]
  )

  /** 시트 윗줄에 쓸 이름 — 고른 것 중 첫 곳 */
  const firstChosenName = useMemo(
    () =>
      rows
        .flatMap((r) => r.candidates)
        .find((p) => selected.has(p.kakao_place_id))?.name,
    [rows, selected]
  )

  /**
   * 어디에 담을지 먼저 묻는다.
   *
   * 예전에는 바로 새 플레이트리스트를 만들었다. 링크를 넣을 때마다 리스트가
   * 하나씩 늘어 금방 지저분해진다 — 대부분은 '저장한 장소' 에만 넣고 싶다.
   */
  /*
   * 담기를 누르면 **먼저 저장하고** 시트를 연다.
   *
   * 저장 버튼(BookmarkButton)이 하는 것과 같은 순서다 — 저장 전이면 누르는
   * 즉시 저장하고, 어느 리스트에 넣을지는 시트에서 고른다. 그래야 시트에
   * 완료 버튼이 필요 없다.
   */
  const handleSave = async () => {
    if (chosenIds.length === 0 || isSaving) return

    setIsSaving(true)
    try {
      const res = await bookmarkApi.saveInstagramPlaces(url, chosenIds)
      setSavedPlaceIds(res.place_ids.map(Number))
      // 알림은 시트를 닫을 때 한 번만. 여기서 띄우면 아직 리스트를 고르는
      // 중인데 "저장됐어요" 가 떠서 끝난 줄 알게 된다.
      setIsDestSheetOpen(true)
    } catch (e: any) {
      toastUtil.error(e?.response?.data?.message ?? '저장하지 못했어요')
    } finally {
      setIsSaving(false)
    }
  }

  /** 시트를 닫으면 저장한 장소로 보낸다 — 담는 일은 거기서 끝났다 */
  const handleSheetClose = ({ pickedAny }: { pickedAny: boolean }) => {
    setIsDestSheetOpen(false)
    // 리스트에 담았으면 그때마다 "○○에 담았어요" 를 봤다. 요약까지 띄우면
    // 두 토스트가 겹친다. 아무것도 안 고르고 닫은 경우에만 알린다.
    if (!pickedAny && savedPlaceIds.length > 0) {
      toastUtil.success(`${savedPlaceIds.length}곳을 저장했어요`)
    }
    navigate(myUserId ? `/user/${myUserId}/saved-places` : '/main-home', {
      replace: true,
    })
  }

  /*
   * 담기 시트는 본문보다 먼저 그린다.
   *
   * 아래 본문은 상태에 따라 일찍 return 한다(주소 없음·오류·불러오는 중).
   * 시트를 그 뒤에 두면, 리스트를 만들고 돌아왔을 때처럼 **본문이 없는
   * 상태**에서는 시트까지 같이 사라진다 — 빈 화면만 남는다.
   */
  const sheet = (
    <SaveDestinationSheet
      isOpen={isDestSheetOpen}
      onClose={handleSheetClose}
      placeIds={savedPlaceIds}
      placeName={firstChosenName}
      justCreatedCategoryId={justCreatedId}
      returnKey={`instagram:${url}`}
    />
  )

  if (url === '') {
    return <Message text="인스타그램에서 공유해 주세요" />
  }
  if (error) {
    return <Message text={error} />
  }
  if (!data) {
    return (
      <Shell>
        {/*
          스피너만 둔다. 점 세 개가 이미 '기다리는 중'을 말하고 있어서,
          같은 말을 글로 한 번 더 하면 화면만 복잡해진다.

          -mt-20px 는 Shell 의 pt-20px 를 되돌린다. 그게 남아 있으면 스피너가
          20px 아래로 내려앉아 가운데가 아니다. 기다리는 동안은 이 화면에
          이것 하나뿐이라 정확히 가운데 있어야 한다.
        */}
        <div className="-mt-20px flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
        {sheet}
      </Shell>
    )
  }

  if (rows.length === 0) {
    return (
      <Shell>
        <Source post={data.post} />
        <div className="mt-24px flex-1 overflow-y-auto scrollbar-hide">
          <p className="text-style-button2 text-brown-80%-main">
            가져올 장소를 찾지 못했어요
          </p>
          <Gap height={8} />
          <p className="text-14px leading-20px text-textColorText3">
            가게 이름이 글에 안 적혀 있거나, 댓글에만 있는 경우예요. 직접
            검색해서 담을 수 있어요.
          </p>
        </div>
        <Footer
          label="직접 검색하기"
          disabled={false}
          onPrimary={() => navigate('/search')}
        />
      </Shell>
    )
  }

  const allChecked =
    rows.length > 0 &&
    rows.every((r) => r.candidates.some((p) => selected.has(p.kakao_place_id)))

  /** 전체 선택/해제. 후보가 여럿인 상호는 첫 곳만 켠다 — 한 상호에 한 곳이다. */
  const toggleAll = () => {
    setSelected(
      allChecked
        ? new Set()
        : new Set(rows.map((r) => r.candidates[0].kakao_place_id))
    )
  }

  return (
    <Shell>
      <Source post={data.post} />

      <div className="mt-20px flex-1 overflow-y-auto scrollbar-hide">
        <button
          type="button"
          onClick={toggleAll}
          className="mb-8px text-14px text-4B3621-80% font-bold"
        >
          {allChecked ? '전체 해제' : '전체 선택'}
        </button>

        <div className="flex flex-col gap-8px pb-16px">
          {rows.map((r, i) => (
            <Group
              key={`${r.name}-${i}`}
              result={r}
              selected={selected}
              onToggle={toggle}
              onPickOne={pickOne}
            />
          ))}
        </div>
      </div>

      <Footer
        label={
          chosenIds.length > 0
            ? `${chosenIds.length}곳 저장하기`
            : '저장할 곳을 골라주세요'
        }
        disabled={chosenIds.length === 0 || isSaving}
        onPrimary={handleSave}
      />

      {/*
        어디에 담을지 고르는 시트 (저장한 장소 / 플레이트리스트).
        닫기만 하면 아무 일도 안 일어난다 — 고른 것은 그대로 남는다.
      */}
      {sheet}
    </Shell>
  )
}

/** 하단 알약 버튼. 온보딩 '가본 곳 불러오기'와 같은 모양이다. */
const Footer: React.FC<{
  label: string
  disabled: boolean
  onPrimary: () => void
}> = ({ label, disabled, onPrimary }) => (
  <div className="pb-24px pt-8px flex flex-col gap-12px">
    <button
      type="button"
      onClick={onPrimary}
      disabled={disabled}
      className={`w-full py-16px rounded-full font-pretendard font-700 text-16px ${
        disabled ? 'bg-E8E1D9 text-[#918063]' : 'bg-4B3621-80% text-white'
      }`}
    >
      {label}
    </button>
  </div>
)

/**
 * 화면 뼈대.
 *
 * 제목은 다른 화면들처럼 앱바에 둔다(아래 선 포함). 본문은 온보딩
 * '가본 곳 불러오기'(OnboardingImportRank)와 같은 모양을 쓴다 —
 * 체크박스가 붙은 카드 목록, 아래 알약 버튼. 하는 일이 같은 화면이 둘이면
 * 생김새도 같아야 한다.
 */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
    <AppBar title="인스타그램에서 가져오기" isBottomBorder />
    <div className="app-container flex-1 min-h-0 flex flex-col px-20px pt-20px overflow-hidden">
      {children}
    </div>
  </div>
)

/** 어느 글에서 왔는지. 링크 입력칸이 있던 자리를 대신한다. */
const Source: React.FC<{ post: InstagramResolveResponse['post'] }> = ({
  post,
}) => (
  <div className="w-full flex items-center gap-12px">
    {post.thumbnail && (
      <img
        src={post.thumbnail}
        alt=""
        draggable={false}
        className="w-48px h-48px rounded-8px object-cover shrink-0 select-none"
        /* 인스타 CDN 이 referrer 를 보면 막는 경우가 있다 */
        referrerPolicy="no-referrer"
      />
    )}
    <span className="text-style-button2 text-brown-80%-main text-ellipsis-1">
      {post.author ? `@${post.author}` : '인스타그램 글'}
    </span>
  </div>
)

/**
 * 상호 하나에 대한 묶음.
 *
 * 후보가 하나면 줄 하나만 그린다. 상호를 머리말로 또 적으면 같은 말이 두 번
 * 나온다. 여럿일 때만 어느 상호의 후보인지 알려 준다.
 */
const Group: React.FC<{
  result: InstagramResult
  selected: Set<string>
  onToggle: (id: string) => void
  onPickOne: (group: InstagramPlace[], id: string) => void
}> = ({ result, selected, onToggle, onPickOne }) => {
  const many = result.candidates.length > 1

  return (
    <div className="w-full flex flex-col gap-12px">
      {many && (
        <p className="font-pretendard font-regular text-12px leading-125% text-textColorText3">
          ‘{result.name}’ 중 한 곳을 골라주세요
        </p>
      )}
      {result.candidates.map((p) => (
        <PlaceRow
          key={p.kakao_place_id}
          place={p}
          checked={selected.has(p.kakao_place_id)}
          onClick={() =>
            many
              ? onPickOne(result.candidates, p.kakao_place_id)
              : onToggle(p.kakao_place_id)
          }
        />
      ))}
    </div>
  )
}

/** 장소 카드 한 장. 온보딩 '가본 곳 불러오기'와 같은 모양이다. */
const PlaceRow: React.FC<{
  place: InstagramPlace
  checked: boolean
  onClick: () => void
}> = ({ place, checked, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={checked}
    className="w-full flex items-center gap-12px px-12px py-12px rounded-12px bg-472410-5% text-left"
  >
    <img
      src={
        checked
          ? '/images/checkbox-bold-filled.png'
          : '/images/checkbox-bold-unchecked.png'
      }
      alt=""
      className="flex-shrink-0 w-22px h-22px object-contain"
    />
    <span className="flex-1 min-w-0">
      <span className="block text-style-button2 text-brown-80%-main text-ellipsis-1">
        {place.name}
      </span>
      <span className="block text-13px text-textColorText3 text-ellipsis-1">
        {place.address ? shortenSido(place.address) : place.category}
      </span>
    </span>
  </button>
)

/**
 * 담지 못한 곳이 있으면 한 번만 알린다.
 *
 * 조용히 사라지면 사용자는 "7곳이라더니 왜 6곳이지"를 알 길이 없다. 그렇다고
 * 목록 중간에 끼우면 고르는 흐름이 끊긴다. 그래서 화면 밖으로 뺐다.
 *
 * 이유는 말하지 않는다. '음식점이 아니다'는 카카오 분류를 그대로 옮긴 말인데
 * 그 분류가 틀릴 때가 있다(카페 '녹취록'이 '농업'으로 돼 있다). 틀린 이유를
 * 단정해 말하느니 몇 곳인지만 알리는 편이 정직하다.
 */
function notifyExcluded(results: InstagramResult[]) {
  const missed = results.filter(
    (r) => r.status === 'not_dining' || r.status === 'not_found'
  ).length

  if (missed > 0) {
    toastUtil.info(`${missed}군데를 찾을 수 없었어요`)
  }
}

/**
 * 한 줄만 화면 한가운데.
 *
 * 전에는 제목과 설명 두 줄이었는데, 둘이 거의 같은 말이었다
 * ("읽지 못했어요" 아래에 "링크를 읽지 못했어요"). 한 줄로 줄인다.
 *
 * -mt-20px 는 Shell 의 pt-20px 를 되돌린다. 그게 남아 있으면 글이 20px
 * 아래로 내려앉아 가운데가 아니다 — 기다리는 중 스피너와 같은 처리다.
 */
const Message: React.FC<{ text: string }> = ({ text }) => (
  <Shell>
    <div className="-mt-20px flex-1 flex items-center justify-center">
      <p className="text-14px leading-20px text-textColorText3 text-center">
        {text}
      </p>
    </div>
  </Shell>
)

export default InstagramShare
